import { navigate } from 'gatsby-plugin-intl';
import React from 'react';
import { Grid, Heading, Select, Text, Box } from 'theme-ui';
import { useTheme } from '~/gatsby-plugin-theme-ui';
import DesktopOrMobileImage from './DesktopOrMobileImage';
import { getDesktopGridTemplateAreas, getMobileGridTemplateAreas, getTabletGridTemplateAreas } from './utils';
import HeaderCTA from './CTA';
import BulletPoints from './BulletPoints';
import { useLocation } from '@reach/router';

type Props = {
  section: Queries.ActivityPageHeaderFragment;
  allActivities: { title: string; slug: string }[];
  secondaryColor: string;
};

const ActivityPageHeader = ({ section, allActivities, secondaryColor }: Props) => {
  const { theme } = useTheme();
  const location = useLocation();

  const {
    heading,
    copy,
    CTA,
    ctaSubtext,
    bulletPoints,
    primaryImage,
    primaryImageMobile,
    secondaryImages,
    secondaryImagesMobile,
  } = section;
  const [secondImage, thirdImage, fourthImage] = secondaryImages ?? [];
  const [secondImageMobile, thirdImageMobile, fourthImageMobile] = secondaryImagesMobile ?? [];
  const handleSelect = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    await navigate(`/activities/${e.target.value}/`);
  };
  const sortedActivities = allActivities.sort((a, b) => a.title.localeCompare(b.title));
  return (
    <Grid
      sx={{
        py: theme.space.xxl,
        px: [theme.space['3md'], theme.space.lg],
        gridTemplateColumns: ['1fr 1fr', '4fr 3.2fr 2.3fr'],
        gridTemplateAreas: [
          getMobileGridTemplateAreas(secondaryImagesMobile?.length ?? secondaryImages?.length ?? 1),
          getTabletGridTemplateAreas(secondaryImages?.length ?? 1),
          getDesktopGridTemplateAreas(secondaryImages?.length ?? 1),
        ],
        rowGap: ['3.2rem', '4rem'],
        columnGap: ['1.6rem', '4rem'],
        maxWidth: 'var(--max-width)',
        mx: 'auto',
      }}
    >
      <Box
        sx={{
          gridArea: 'dropdown',
          mb: [0, '4rem'],
          mt: [0, '2.4rem', 0],
          width: ['278px', '309px'],
          position: 'relative',
          mx: ['auto', 0],
          maxHeight: '40px',
        }}
      >
        <Select
          defaultValue={allActivities.find((activity) => location.pathname.includes(activity.slug))?.slug}
          onChange={handleSelect}
          arrow={
            <svg
              sx={{
                position: 'absolute',
                top: '50%',
                right: '2.4rem',
                translate: '0 -50%',
                pointerEvents: 'none',
              }}
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path d="M6 9L12 15L18 9" stroke="black" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          }
          sx={{
            fontFamily: 'body',
            px: '2.4rem',
            py: '0.8rem',
            maxHeight: '40px',
            borderRadius: '100px',
            fontSize: '1.6rem',
            lineHeight: 1.15,
            fontWeight: 400,
          }}
        >
          {sortedActivities.map((activity) => (
            <option key={activity.slug} value={activity.slug}>
              {activity.title}
            </option>
          ))}
        </Select>
      </Box>
      <Box sx={{ gridArea: 'text', maxWidth: '437px', mb: [0, '8px', 0], mx: ['auto', 0] }}>
        {heading && (
          <Heading
            as="h1"
            sx={{
              fontSize: '3.6rem',
              mb: ['3.2rem', '2.4rem'],
              lineHeight: 1,
              textTransform: 'uppercase',
              letterSpacing: 0,
              textAlign: ['center', 'left'],
            }}
            dangerouslySetInnerHTML={{ __html: heading }}
          />
        )}

        {copy?.childMarkdownRemark?.html && (
          <Text
            as="div"
            variant="text.paragraphMain"
            dangerouslySetInnerHTML={{ __html: copy.childMarkdownRemark.html }}
            sx={{
              p: { m: 0 },
              mb: [0, null, '1.6rem'],
            }}
          />
        )}
      </Box>
      <DesktopOrMobileImage
        desktopImage={primaryImage}
        mobileImage={primaryImageMobile}
        forwardSx={{ gridArea: 'primaryImage', mb: [0, '8px', 0] }}
      />
      <HeaderCTA CTA={CTA} ctaSubtext={ctaSubtext} />
      <DesktopOrMobileImage
        desktopImage={secondImage}
        mobileImage={secondImageMobile}
        forwardSx={{ gridArea: 'secondImage', mt: [0, '8px', 0] }}
      />

      <BulletPoints bulletPoints={bulletPoints} backgroundColor={secondaryColor} />
      <DesktopOrMobileImage
        desktopImage={thirdImage}
        mobileImage={thirdImageMobile}
        forwardSx={{ gridArea: 'thirdImage' }}
      />
      <DesktopOrMobileImage
        desktopImage={fourthImage}
        mobileImage={fourthImageMobile}
        forwardSx={{ gridArea: 'fourthImage' }}
      />
    </Grid>
  );
};

export default ActivityPageHeader;
